import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api, API_BASE_URL } from '../../core/apiClient.ts';

type Batch = {
  id: string;
  status: string;
  created_at: string;
  scheduled_at: string | null;
  launches_count: number;
  success_count: number;
  error_count: number;
  resumable_count: number;
  template_name?: string;
  social_account_names?: string | null;
  fan_page_id?: string | null;
  fan_page_name?: string | null;
};
type Launch = { social_account_id?: string | null; social_account_name?: string | null; id: number; ad_account_id: string; status: string; error_message: string | null; campaign_name?: string | null; adset_name?: string | null; ad_name?: string | null; campaign_id?: string | null; adset_id?: string | null; ad_id?: string | null };
type Log = { step: string; method: string; url: string; request_body: string; response_status: number; response_body: string; created_at: string };

function formatLog(logs: Log[]): string {
  return logs
    .map((log) => {
      let req = '';
      try { req = JSON.stringify(JSON.parse(log.request_body), null, 2); } catch { req = log.request_body; }
      let res = '';
      try { res = JSON.stringify(JSON.parse(log.response_body), null, 2); } catch { res = log.response_body; }
      return `${log.step} · ${log.response_status}\n${req}\n${res}`;
    })
    .join('\n\n---\n\n');
}

function formatDateUa(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  try {
    const iso = dateStr.includes('T') ? dateStr : `${dateStr.replace(' ', 'T')}Z`;
    return new Date(iso).toLocaleString('uk-UA', {
      timeZone: 'Europe/Kiev',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

const STATUS_COLORS: Record<string, string> = {
  completed: 'var(--color-success)',
  running: 'var(--color-primary)',
  queued: 'var(--color-warning)',
  stopped: 'var(--color-text-muted)',
  completed_with_errors: 'var(--color-danger)',
  error: 'var(--color-danger)',
  success: 'var(--color-success)',
  skipped: 'var(--color-text-subtle)',
  cancelled: 'var(--color-text-subtle)',
  pending: 'var(--color-warning)',
};

export function QueuePage() {
  const location = useLocation();
  const successMessage = (location.state as { message?: string } | null)?.message;

  const [batches, setBatches] = useState<Batch[]>([]);
  const [launches, setLaunches] = useState<Launch[]>([]);
  const [open, setOpen] = useState('');
  const [logs, setLogs] = useState<Log[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeLaunchId, setActiveLaunchId] = useState<number | null>(null);

  const load = useCallback(() => api.get<Batch[]>('/api/launcher/batches').then(setBatches), []);
  const eventRefreshTimer = useRef<number | null>(null);

  useEffect(() => {
    load().catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    const source = new EventSource(`${API_BASE_URL}/api/launcher/events`);
    const refresh = () => {
      if (eventRefreshTimer.current !== null) return;
      eventRefreshTimer.current = window.setTimeout(async () => {
        eventRefreshTimer.current = null;
        await load().catch((e) => setError(e.message));
        if (open) api.get<Launch[]>(`/api/launcher/batches/${open}/launches`).then(setLaunches).catch(() => {});
        if (activeLaunchId) api.get<Log[]>(`/api/launcher/launches/${activeLaunchId}/logs`).then(setLogs).catch(() => {});
      }, 150);
    };
    source.addEventListener('queue-change', refresh);
    return () => { source.close(); if (eventRefreshTimer.current !== null) { window.clearTimeout(eventRefreshTimer.current); eventRefreshTimer.current = null; } };
  }, [load, open, activeLaunchId]);

  // SSE updates immediately when the Durable Object still has a live subscriber.
  // A Durable Object may be evicted between alarm runs, however, so keep a slow
  // safety refresh only while execution can actually change the screen.
  useEffect(() => {
    const hasActiveBatch = batches.some((batch) => ['queued', 'running'].includes(batch.status));
    if (!hasActiveBatch) return;

    const refreshActiveQueue = () => {
      load().catch(() => {});
      if (open) api.get<Launch[]>(`/api/launcher/batches/${open}/launches`).then(setLaunches).catch(() => {});
      if (activeLaunchId) api.get<Log[]>(`/api/launcher/launches/${activeLaunchId}/logs`).then(setLogs).catch(() => {});
    };
    const intervalId = window.setInterval(refreshActiveQueue, 8_000);
    return () => window.clearInterval(intervalId);
  }, [batches, load, open, activeLaunchId]);

  const toggle = async (batch: Batch) => {
    const next = open === batch.id ? '' : batch.id;
    setOpen(next);
    setLaunches([]);
    if (next) { try { setLaunches(await api.get<Launch[]>(`/api/launcher/batches/${batch.id}/launches`)); } catch (e) { setError(e instanceof Error ? e.message : 'Could not load launches'); } }
  };

  const act = async (path: string) => {
    if (busy) return;
    setBusy(path); setError('');
    try {
      await api.post(path);
      await load();
      if (open) setLaunches(await api.get<Launch[]>(`/api/launcher/batches/${open}/launches`));
    } catch (e) { setError(e instanceof Error ? e.message : 'Queue action failed'); }
    finally { setBusy(null); }
  };

  const copyLogs = async () => {
    if (!logs) return;
    try {
      await navigator.clipboard.writeText(formatLog(logs));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = formatLog(logs);
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="page-body launch-queue">
      <div className="templates-head">
        <div>
          <h1>Launch queue</h1>
          <p className="text-muted text-sm">Batches and execution logs. History is kept for 7 days.</p>
        </div>
        <button className="btn btn--secondary" onClick={() => load().catch((e) => setError(e.message))}>Refresh</button>
      </div>

      {successMessage && (
        <div className="notification notification--success">{successMessage}</div>
      )}

      {error && <div className="notification notification--error" role="alert">{error}</div>}
      <div className="queue-overview" aria-label="Queue overview">
        <div><strong>{batches.filter((b) => ['queued', 'running'].includes(b.status)).length}</strong><span>Active batches</span></div>
        <div><strong>{batches.reduce((n, b) => n + b.success_count, 0)}</strong><span>Successful launches</span></div>
        <div><strong>{batches.reduce((n, b) => n + b.error_count, 0)}</strong><span>Errors to retry</span></div>
      </div>
      <p className="text-muted text-sm">Stop pauses pending work after the current launch finishes. Resume restores stopped or cancelled launches. Retry only restarts errors and reuses saved Meta IDs.</p>
      <div className="queue-table-scroll">
      <table className="data-table queue-batches">
        <colgroup>{[6, 10, 9, 9, 12, 11, 11, 5, 5, 5, 17].map((width, index) => <col key={index} style={{ width: `${width}%` }} />)}</colgroup>
        <thead>
          <tr>
            <th>Soc</th>
            <th>Batch</th>
            <th>Template</th>
            <th>Fan Page</th>
            <th>Status</th>
            <th>Created At</th>
            <th>Start Time</th>
            <th>Total</th>
            <th>Success</th>
            <th>Errors</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {batches.map((batch) => (
            <React.Fragment key={batch.id}>
              <tr className={`queue-row queue-row--${batch.status}`}>
                <td title={batch.social_account_names ?? undefined}>{batch.social_account_names ?? '—'}</td>
                <td><button className="btn btn--ghost btn--sm font-mono" aria-expanded={open === batch.id} onClick={() => void toggle(batch)}>{open === batch.id ? '-' : '+'} {batch.id.slice(0, 8)}...</button></td>
                <td style={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={batch.template_name}>{batch.template_name ?? '—'}</td>
                <td style={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={batch.fan_page_name ?? batch.fan_page_id ?? undefined}>
                  {batch.fan_page_name
                    ? <span style={{ fontSize: '0.8125rem' }}>{batch.fan_page_name}</span>
                    : batch.fan_page_id
                      ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{batch.fan_page_id}</span>
                      : <span style={{ color: 'var(--color-text-subtle)' }}>—</span>
                  }
                </td>
                <td>
                  <span style={{
                    fontSize: '0.72rem', padding: '2px 7px', borderRadius: 4, fontWeight: 600,
                    background: `color-mix(in srgb, ${STATUS_COLORS[batch.status] ?? 'var(--color-text-muted)'} 15%, transparent)`,
                    color: STATUS_COLORS[batch.status] ?? 'var(--color-text-muted)',
                    whiteSpace: 'nowrap',
                  }}>
                    {batch.status}
                  </span>
                </td>
                <td className="text-muted text-sm" style={{ whiteSpace: 'nowrap' }}>{formatDateUa(batch.created_at) ?? '—'}</td>
                <td className="text-muted text-sm" style={{ whiteSpace: 'nowrap' }}>{formatDateUa(batch.scheduled_at) ?? '—'}</td>
                <td>{batch.launches_count}</td>
                <td style={{ color: 'var(--color-success)', fontWeight: 600 }}>{batch.success_count}</td>
                <td style={{ color: batch.error_count > 0 ? 'var(--color-danger)' : 'inherit', fontWeight: batch.error_count > 0 ? 600 : 400 }}>{batch.error_count}</td>
                <td>
                  <div className="queue-batch-actions">
                    <button className="btn btn--secondary btn--sm" disabled={!!busy || !['queued', 'running'].includes(batch.status)} onClick={() => void act(`/api/launcher/batches/${batch.id}/stop`)} title="Pause pending launches after the current launch finishes">Stop / Pause</button>
                    <button className="btn btn--secondary btn--sm" disabled={!!busy || !(batch.resumable_count > 0 || batch.status === 'stopped')} onClick={() => void act(`/api/launcher/batches/${batch.id}/resume`)}>Resume</button>
                    <button className="btn btn--secondary btn--sm" disabled={!!busy || !batch.error_count} onClick={() => void act(`/api/launcher/batches/${batch.id}/retry`)}>Retry errors</button>
                  </div>
                </td>
              </tr>
              {open === batch.id && (
                <tr>
                  <td colSpan={11}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Soc</th>
                          <th>Account</th>
                          <th>Status</th>
                          <th>Error</th>
                          <th>Controls / Log</th>
                          <th>Campaign structure</th>
                        </tr>
                      </thead>
                      <tbody>
                        {launches.map((launch) => (
                          <tr className={`queue-row queue-row--${launch.status}`} key={launch.id}>
                            <td title={launch.social_account_id ?? undefined}>{launch.social_account_name?.trim() || launch.social_account_id || '—'}</td>
                            <td><span className="queue-account-id">{launch.ad_account_id}<AccountLink accountId={launch.ad_account_id} onError={setError} /></span></td>
                            <td>
                              <span style={{
                                fontSize: '0.72rem', padding: '2px 7px', borderRadius: 4, fontWeight: 600,
                                background: `color-mix(in srgb, ${STATUS_COLORS[launch.status] ?? 'var(--color-text-muted)'} 15%, transparent)`,
                                color: STATUS_COLORS[launch.status] ?? 'var(--color-text-muted)',
                              }}>
                                {launch.status}
                              </span>
                            </td>
                            <td style={{ color: 'var(--color-danger)', fontSize: '0.8125rem' }}>{launch.error_message ?? ''}</td>
                            <td>
                              <div className="queue-launch-actions">
                                <button className="btn btn--secondary btn--sm" disabled={!!busy || launch.status !== 'pending'} onClick={() => void act(`/api/launcher/launches/${launch.id}/stop`)}>Stop</button>
                                <button className="btn btn--secondary btn--sm" disabled={!!busy || !['stopped', 'cancelled'].includes(launch.status)} onClick={() => void act(`/api/launcher/launches/${launch.id}/resume`)}>Resume</button>
                                <button className="btn btn--secondary btn--sm" disabled={!!busy || !['pending', 'stopped'].includes(launch.status)} onClick={() => void act(`/api/launcher/launches/${launch.id}/cancel`)}>Cancel</button>
                                <button className="btn btn--secondary btn--sm" disabled={!!busy || launch.status !== 'error'} onClick={() => void act(`/api/launcher/launches/${launch.id}/retry`)}>Retry</button>
                                <button className="btn btn--secondary btn--sm" onClick={async () => {
                                  try { setCopied(false); setActiveLaunchId(launch.id); setLogs(await api.get<Log[]>(`/api/launcher/launches/${launch.id}/logs`)); }
                                  catch (e) { setError(e instanceof Error ? e.message : 'Could not load logs'); }
                                }}>Log</button>
                              </div>
                            </td>
                            <td><LaunchStructure launch={launch} /></td>
                          </tr>
                        ))}
                        {!launches.length && <tr><td colSpan={6} className="text-muted">Loading launches...</td></tr>}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {!batches.length && <tr><td colSpan={11} className="queue-empty">{loading ? 'Loading queue...' : 'No launches yet. Create a batch in the launch wizard to see its progress here.'}</td></tr>}
        </tbody>
      </table>
      </div>

      {logs && (
        <div className="queue-drawer">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <button className="btn btn--secondary" onClick={() => { setLogs(null); setActiveLaunchId(null); }}>Close</button>
            <button className="btn btn--secondary" onClick={copyLogs}>
              {copied ? '✓ Copied!' : '📋 Copy log'}
            </button>
          </div>
          <h2>Launch logs</h2>
          {logs.length ? logs.map((log, i) => (
            <details key={i} open>
              <summary>{log.step} · {log.response_status}</summary>
              <pre>{log.request_body}</pre>
              <pre>{log.response_body}</pre>
            </details>
          )) : <p>No logs for this launch.</p>}
        </div>
      )}
    </div>
  );
}

function LaunchStructure({ launch }: { launch: Launch }) {
  if (!launch.campaign_name && !launch.campaign_id) return <span className="text-muted">Not created yet</span>;
  return <details className="queue-structure">
    <summary title={launch.campaign_id ?? undefined}><small>Campaign</small>{launch.campaign_name ?? launch.campaign_id}</summary>
    {launch.adset_name || launch.adset_id ? <details>
      <summary title={launch.adset_id ?? undefined}><small>Ad set</small>{launch.adset_name ?? launch.adset_id}</summary>
      {launch.ad_name || launch.ad_id ? <div title={launch.ad_id ?? undefined}><small>Ad</small>{launch.ad_name ?? launch.ad_id}</div> : <p className="text-muted">Ad not created yet</p>}
    </details> : <p className="text-muted">Ad set not created yet</p>}
  </details>;
}

function AccountLink({ accountId, onError }: { accountId: string; onError: (error: string) => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const date = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    const params = new URLSearchParams({ act: accountId.replace(/^act_/, ''), attribution_windows: 'default', business_id: '208668791362846', columns: 'name,delivery,budget,results,cost_per_result,impressions,spend,ctr,cpm,cpc,clicks,actions:lead,cost_per_action_type:lead,actions:omni_complete_registration,cost_per_action_type:omni_complete_registration', date: `${date}_${date}`, global_scope_id: '208668791362846', sort: 'spend~0' });
    try {
      await navigator.clipboard.writeText(`https://adsmanager.facebook.com/adsmanager/manage/ads?${params}`);
      setCopied(true); window.setTimeout(() => setCopied(false), 1800);
    } catch { onError('Could not copy the Ads Manager link. Check clipboard permissions.'); }
  };
  return <button className="btn btn--ghost btn--sm queue-copy-link" onClick={() => void copy()} aria-label="Copy Ads Manager link" title={copied ? 'Copied' : 'Copy Ads Manager link'}>{copied ? '✓' : <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><rect x="5" y="5" width="8" height="9" rx="1" /><path d="M10 5V2H2v9h3" /></svg>}</button>;
}
